import React from 'react';
/**
 * Orientation-aware roving-focus keyboard navigation for a list or grid of
 * focusable items.
 *
 * `count` is the number of focusable items in visual order; `columns` is the
 * layout column count. A grid passes its real column count (e.g. 3) so all
 * four arrows move with column wrapping. A vertical menu passes `columns: 1`,
 * which collapses horizontal arrows to no-ops and lets ArrowUp/ArrowDown move
 * between adjacent items. Home/End jump to the ends, Enter/Space activate the
 * focused item, and the active item is the only one with tabIndex 0.
 */
export const useRovingFocus = (count, columns) => {
    const [activeIndex, setActiveIndex] = React.useState(0);
    const itemRefs = React.useRef([]);
    const isHorizontal = columns > 1;
    // Keep the active index within bounds as the visible item set changes.
    React.useEffect(() => {
        setActiveIndex((current) => {
            if (count === 0) {
                return 0;
            }
            return Math.min(current, count - 1);
        });
    }, [count]);
    const focusItem = React.useCallback((index) => {
        const target = itemRefs.current[index];
        if (target) {
            target.focus();
        }
    }, []);
    const moveTo = React.useCallback((index) => {
        if (index < 0 || index >= count) {
            return;
        }
        setActiveIndex(index);
        focusItem(index);
    }, [count, focusItem]);
    const registerItem = React.useCallback((index) => (element) => {
        itemRefs.current[index] = element;
    }, []);
    const focusFirstItem = React.useCallback(() => {
        if (count === 0) {
            return;
        }
        moveTo(0);
    }, [count, moveTo]);
    const handleItemKeyDown = React.useCallback((index) => (event) => {
        switch (event.key) {
            case 'ArrowRight':
                if (!isHorizontal) {
                    break;
                }
                event.preventDefault();
                moveTo(Math.min(index + 1, count - 1));
                break;
            case 'ArrowLeft':
                if (!isHorizontal) {
                    break;
                }
                event.preventDefault();
                moveTo(Math.max(index - 1, 0));
                break;
            case 'ArrowDown':
                event.preventDefault();
                moveTo(Math.min(index + columns, count - 1));
                break;
            case 'ArrowUp':
                event.preventDefault();
                moveTo(Math.max(index - columns, 0));
                break;
            case 'Home':
                event.preventDefault();
                moveTo(0);
                break;
            case 'End':
                event.preventDefault();
                moveTo(count - 1);
                break;
            case 'Enter':
            case ' ':
            case 'Spacebar':
                event.preventDefault();
                event.currentTarget.click();
                break;
            default:
                break;
        }
    }, [columns, count, isHorizontal, moveTo]);
    const getTabIndex = React.useCallback((index) => (index === activeIndex ? 0 : -1), [activeIndex]);
    return { activeIndex, getTabIndex, registerItem, handleItemKeyDown, focusFirstItem };
};
//# sourceMappingURL=useRovingFocus.js.map